import React, { createContext, useContext, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const RunNotifierContext = createContext(null);

export function RunNotifierProvider({ children }) {
  const jobs = useRef(new Map()); // jobId -> { agentName, stream }

  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return "denied";
    const result = await Notification.requestPermission();
    return result;
  }, []);

  const registerJob = useCallback((jobId, agentName, target, agentId) => {
    if (jobs.current.has(jobId)) return;

    const stream = api.streamExecution(
      jobId,
      (data) => {
        if (data.type !== "complete") return;

        const succeeded = data.status === "completed";
        const title = succeeded ? "Analysis complete" : "Analysis failed";
        const body = target
          ? `${agentName} — ${target}`
          : agentName;
        const toastMsg = succeeded
          ? `${body} completed successfully`
          : `${body} failed`;

        if (succeeded) {
          toast.success(toastMsg, { position: "top-right", autoClose: 6000 });
        } else {
          toast.error(toastMsg, { position: "top-right", autoClose: 8000 });
        }

        if ("Notification" in window && Notification.permission === "granted") {
          const notif = new Notification(title, {
            body: toastMsg,
            icon: "/favicon.ico",
          });
          if (agentId) {
            notif.onclick = () => {
              window.focus();
              window.location.href = `/agent/${agentId}?executionId=${jobId}&from=history`;
            };
          }
        }

        jobs.current.get(jobId)?.stream?.close();
        jobs.current.delete(jobId);
      },
      () => {
        jobs.current.delete(jobId);
      },
    );

    jobs.current.set(jobId, { agentName, stream });
  }, []);

  return (
    <RunNotifierContext.Provider value={{ registerJob, requestPermission }}>
      {children}
    </RunNotifierContext.Provider>
  );
}

export function useRunNotifier() {
  return useContext(RunNotifierContext);
}
